import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

import { SubscriptionSnapshot } from '../models/subscription.snapshot.model';
import { CatalogVocabulary } from '../models/catalog.plan.model';
import { ApiDigitalAuthSubscription } from '../services/auth/subscription.service';
import { authApiKey } from '../scripts/authentication.script';
import { can, CanResult } from '../scripts/subscription.can';
import { guardSnapshotResponse } from '../scripts/response.shape.guard';

const STORAGE_KEY = 'da_subscription_snapshot';

function loadFromSession(): SubscriptionSnapshot | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveToSession(snapshot: SubscriptionSnapshot | null): void {
  try {
    if (snapshot) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // sessionStorage full or unavailable — continue without persistence
  }
}

interface SubscriptionContextState {
  snapshot: SubscriptionSnapshot | null;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
  seedSnapshot: (data: SubscriptionSnapshot | null) => void;
  can: (action: string, arn: string) => CanResult;
  setVocabulary: (v: CatalogVocabulary | null) => void;
}

const SubscriptionContext = createContext<SubscriptionContextState>({
  snapshot: null,
  loading: false,
  refreshSubscription: async () => {},
  seedSnapshot: () => {},
  can: () => ({ allowed: true, reason: null }),
  setVocabulary: () => {},
});

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [snapshot, setSnapshot] = useState<SubscriptionSnapshot | null>(loadFromSession);
  const [loading, setLoading] = useState(false);
  const [vocabulary, setVocabularyState] = useState<CatalogVocabulary | null>(null);
  const didFetch = useRef(false);

  const updateSnapshot = useCallback((data: SubscriptionSnapshot | null) => {
    setSnapshot(data);
    saveToSession(data);
  }, []);

  const fetchSnapshot = useCallback(async () => {
    const apiKey = authApiKey();
    if (!apiKey) return;

    setLoading(true);
    try {
      const response = await ApiDigitalAuthSubscription.getSnapshot(apiKey);
      if (response.status === 200 && response.data) {
        guardSnapshotResponse(response.data);
        updateSnapshot(response.data);
      } else {
        updateSnapshot(null);
      }
    } catch {
      updateSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [updateSnapshot]);

  const seedSnapshot = useCallback((data: SubscriptionSnapshot | null) => {
    if (data) {
      updateSnapshot(data);
    }
  }, [updateSnapshot]);

  const setVocabulary = useCallback((v: CatalogVocabulary | null) => {
    setVocabularyState(v);
  }, []);

  const canCheck = useCallback((action: string, arn: string): CanResult => {
    return can(snapshot, action, arn, vocabulary);
  }, [snapshot, vocabulary]);

  useEffect(() => {
    if (!didFetch.current) {
      didFetch.current = true;
      fetchSnapshot();
    }
  }, [fetchSnapshot]);

  return (
    <SubscriptionContext.Provider value={{
      snapshot,
      loading,
      refreshSubscription: fetchSnapshot,
      seedSnapshot,
      can: canCheck,
      setVocabulary,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export function useSubscription(): SubscriptionContextState {
  return useContext(SubscriptionContext);
}
